import { Button, Card, CardHeader, Spinner, cn, formatTime } from '@minisoc/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api';

const SUGGESTIONS = [
  'Est-ce grave ?',
  'Comment savoir si c’est un faux positif ?',
  'Que risque-t-on si on ne fait rien ?',
];

/**
 * Assistant de levée de doute (§6.5).
 *
 * L'historique est rechargé depuis l'API plutôt que conservé en mémoire : la
 * conversation doit être retrouvée en revenant sur l'alerte, y compris depuis un
 * autre poste, pendant un incident qui peut durer plusieurs heures.
 */
export function AssistantPanel({ alertId }: { alertId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const history = useQuery({
    queryKey: ['llm', alertId],
    queryFn: () => api.getConversation(alertId),
  });

  const send = useMutation({
    mutationFn: (message: string) => api.chat(alertId, message),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['llm', alertId] }),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history.data?.length, send.isPending]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const message = draft.trim();
    if (!message || send.isPending) return;

    setDraft('');
    send.mutate(message);
  }

  const dryRun = send.data?.dryRun === true;

  return (
    <Card className="flex h-[32rem] flex-col">
      <CardHeader
        title="Assistant IA"
        subtitle="Levée de doute guidée"
        icon={
          <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
            <path d="M12 2a2 2 0 0 1 2 2v1h3a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h3V4a2 2 0 0 1 2-2Zm-2.5 8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm5 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" />
          </svg>
        }
      />

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {history.isPending ? (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner />
            Chargement de la conversation…
          </div>
        ) : null}

        {history.data?.length === 0 && !send.isPending ? (
          <div className="space-y-3">
            <p className="text-sm text-text-muted">
              Posez une question sur cette alerte. L'assistant dispose du contexte technique et du
              plan de réaction, et vous indiquera quand contacter MiniSOC.
            </p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => send.mutate(suggestion)}
                  className="rounded-lg px-2.5 py-1.5 text-xs text-brand-400 ring-1 ring-brand-500/30 transition-colors hover:bg-brand-500/10"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {history.data?.map((message) => (
          <div
            key={message.id}
            className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap',
                message.role === 'user'
                  ? 'bg-electric/15 text-text'
                  : 'bg-surface-sunken text-text ring-1 ring-border-subtle',
              )}
            >
              {message.content}
              <span className="mt-1.5 block font-mono text-[0.625rem] text-text-dim">
                {formatTime(message.createdAt)}
              </span>
            </div>
          </div>
        ))}

        {send.isPending ? (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner />
            L'assistant analyse l'alerte…
          </div>
        ) : null}

        {send.isError ? (
          <p role="alert" className="rounded-lg bg-critical/10 px-3 py-2 text-sm text-critical">
            L'assistant est momentanément indisponible. Le plan de réaction ci-contre reste
            applicable.
          </p>
        ) : null}

        <div ref={bottomRef} />
      </div>

      {dryRun ? (
        <p className="border-t border-border-subtle bg-high/5 px-5 py-2 text-xs text-high">
          Assistant IA non activé sur cette installation : les réponses proviennent du référentiel
          MiniSOC.
        </p>
      ) : null}

      <form onSubmit={submit} className="flex gap-2 border-t border-border-subtle px-5 py-3">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Votre question…"
          className="min-w-0 flex-1 rounded-lg border border-border-strong bg-surface-sunken px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-brand-500"
        />
        <Button type="submit" variant="gradient" size="sm" disabled={send.isPending || !draft.trim()}>
          Envoyer
        </Button>
      </form>
    </Card>
  );
}
